import React, { useState, memo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  Alert,
  Button,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
  Fade,
  Slide,
  Avatar,
  Chip,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  InputLabel
} from '@mui/material';
import EmailAutocompleteTextField from '../../EmailAutocompleteTextField';
import {
  Person,
  Email,
  Phone,
  AccountCircle,
  ArrowForward,
  CheckCircle,
  LocationOn,
  Send,
  CleaningServices,
  Home,
  Bed,
  Bathtub,
  Notes,
  Add,
  Remove,
  CalendarToday
} from '@mui/icons-material';
import { useBooking } from '../../../contexts/BookingContext';
import { useAuth } from '../../../contexts/AuthContext';

const GuestInfoStep = memo(({ onContinue, isModal = false, isQuoteMode = false }) => {
  const {
    bookingState,
    updateCustomerInfo,
    clearErrors,
    setGuestMode,
    setCurrentStep,
    markStepCompleted,
    setErrors,
    validateCurrentStep
  } = useBooking();
  
  const { createUser } = useAuth();

  const [createAccount, setCreateAccount] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Questions configuration
  const questions = [
    {
      id: 'fullName',
      question: "Firstly, who do we have the pleasure of serving today?",
      placeholder: "Enter your first and last name",
      icon: <Person />, // You can use a custom icon if desired
      validation: (value) => value && value.trim().split(' ').length >= 2 ? null : 'Please enter your first and last name',
      type: 'text'
    },
    {
      id: 'phone',
      question: (answers) => `Thanks${answers.fullName ? ' ' + answers.fullName.split(' ')[0] : ''}! What's a good number to reach you on?`,
      placeholder: "Phone number",
      icon: <Phone />,
      type: 'phone',
      validation: (value) => {
        if (!value) return 'Phone number is required';
        const digits = value.replace(/\D/g, '');
        return digits.length >= 10 ? null : 'Please enter a valid phone number';
      }
    },
    {
      id: 'email',
      question: (answers) => `Thanks${answers.fullName ? ' ' + answers.fullName : ''}! Before we get started, what's your email address to send your quote to?`,
      placeholder: "Enter your email",
      icon: <Email />,
      validation: (value) => {
        if (!value) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? null : 'Please enter a valid email address';
      },
      type: 'email'
    },
    {
      id: 'residenceType',
      question: "Great! So what type of residence is this?",
      placeholder: "Select residence type",
      icon: <Home />,
      type: 'select',
      options: [
        { value: 'home', label: 'Home' },
        { value: 'office', label: 'Office' },
        { value: 'vacation', label: 'Vacation Rental' },
        { value: 'other', label: 'Other' }
      ],
      validation: (value) => value ? null : 'Please select a residence type'
    },
    {
      id: 'bedrooms',
      question: (answers) => `Thanks${answers.fullName ? ' ' + answers.fullName.split(' ')[0] : ''}, let's get started on your ${answers.residenceType ? answers.residenceType.charAt(0).toUpperCase() + answers.residenceType.slice(1) : 'residence'} cleaning quote! To get started, how many bedrooms? If it's an office just select office below.`,
      placeholder: "Number of bedrooms",
      icon: <Bed />,
      type: 'select',
      options: [
        ...Array.from({ length: 15 }, (_, i) => ({ value: (i + 1).toString(), label: (i + 1).toString() })),
        { value: 'office', label: 'Office' }
      ],
      validation: (value) => value ? null : 'Please select number of bedrooms or office'
    },
    {
      id: 'bathrooms',
      question: (answers) => `Great! How many bathrooms? For office cleanings select office one more time please.`,
      placeholder: "Number of bathrooms",
      icon: <Bathtub />,
      type: 'select',
      options: [
        ...Array.from({ length: 15 }, (_, i) => ({ value: (i + 1).toString(), label: (i + 1).toString() })),
        { value: 'office', label: 'Office' }
      ],
      validation: (value) => value ? null : 'Please select number of bathrooms or office'
    },
    {
      id: 'squareFootage',
      question: (answers) => `What is the square footage of your ${answers.residenceType ? answers.residenceType.charAt(0).toUpperCase() + answers.residenceType.slice(1) : 'residence'}?`,
      placeholder: "Select square footage",
      icon: <Home />,
      type: 'slider',
      min: 1,
      max: 20000,
      step: 100,
      validation: (value) => value ? null : 'Please select square footage'
    },
    {
      id: 'referralSource',
      question: (answers) => `And ${answers.fullName ? answers.fullName.split(' ')[0] : ''}, what made you reach out today?`,
      placeholder: "How did you hear about us?",
      icon: <Person />,
      type: 'checkbox-group',
      options: [
        { value: 'google', label: 'Google' },
        { value: 'fb', label: 'FB' },
        { value: 'nextdoor', label: 'Nextdoor' },
        { value: 'angi', label: 'ANGI' },
        { value: 'yelp', label: 'Yelp' },
        { value: 'insta', label: 'Insta' },
        { value: 'x', label: 'X' },
        { value: 'tiktok', label: 'TikTok' },
        { value: 'referral', label: 'Referral' },
        { value: 'other', label: 'Other' }
      ],
      validation: () => null, // Optional
      skip: true
    },
    {
      id: 'specialOccasion',
      question: (answers) => `Is there any special occasion you're getting this clean done for${answers.fullName ? ' ' + answers.fullName.split(' ')[0] : ''}? Please include any special requests for your ${answers.residenceType ? answers.residenceType.charAt(0).toUpperCase() + answers.residenceType.slice(1) : 'residence'} cleaning below too!`,
      placeholder: "Special occasion or requests",
      icon: <Notes />,
      type: 'checkbox-text',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ],
      validation: () => null, // Optional
      skip: true
    },
    {
      id: 'cleanByDate',
      question: (answers) => `Is there a specific date you need your ${answers.residenceType ? answers.residenceType.charAt(0).toUpperCase() + answers.residenceType.slice(1) : 'residence'} cleaned by?`,
      placeholder: "Select date",
      icon: <CalendarToday />,
      type: 'date',
      validation: () => null // Optional
    },
    {
      id: 'frequency',
      question: (answers) => `Okay we are putting you on our schedule${answers.fullName ? ' ' + answers.fullName.split(' ')[0] : ''}! How often would you like us to clean your ${answers.residenceType ? answers.residenceType.charAt(0).toUpperCase() + answers.residenceType.slice(1) : 'residence'}?`,
      placeholder: "Select frequency",
      icon: <CleaningServices />,
      type: 'select',
      options: [
        { value: 'one-time', label: 'One Time' },
        { value: 'weekly', label: 'Weekly' },
        { value: 'every-other-week', label: 'Every Other Week' },
        { value: 'monthly', label: 'Monthly' },
        { value: '2-3x-per-week', label: '2-3 Times Per Week' },
        { value: 'every-other-day', label: 'Every Other Day' },
        { value: 'every-day', label: 'Every Day' }
      ],
      validation: (value) => value ? null : 'Please select a frequency'
    },
    {
      id: 'dirtiness',
      question: (answers) => `Thanks, and on a scale of 1 to 10${answers.fullName ? ' ' + answers.fullName.split(' ')[0] : ''}, 1 being your ${answers.residenceType ? answers.residenceType.charAt(0).toUpperCase() + answers.residenceType.slice(1) : 'residence'} is spotless, you could eat off the floors and 10 being it is almost unlivable, where would it fall?`,
      placeholder: "How dirty is it?",
      icon: <CleaningServices />,
      type: 'slider',
      min: 1,
      max: 10,
      step: 1,
      validation: (value) => value ? null : 'Please select a dirtiness level'
    },
    {
      id: 'lastProfessionalClean',
      question: (answers) => `When was the last time you had your ${answers.residenceType ? answers.residenceType.charAt(0).toUpperCase() + answers.residenceType.slice(1) : 'residence'} professionally cleaned?`,
      placeholder: "Last professional clean",
      icon: <CleaningServices />,
      type: 'select',
      options: [
        { value: 'never', label: 'Never' },
        { value: 'recently', label: 'Recently' },
        { value: 'not-recently', label: 'Not Recently' }
      ],
      validation: (value) => value ? null : 'Please select an option'
    },
    {
      id: 'decisionMaker',
      question: (answers) => `Gotcha, thanks${answers.fullName ? ' ' + answers.fullName.split(' ')[0] : ''}, Last Question, are you the decision maker for the home or is there someone else that needs to be on board with getting your ${answers.residenceType ? answers.residenceType.charAt(0).toUpperCase() + answers.residenceType.slice(1) : 'residence'} cleaned?`,
      placeholder: "Decision maker",
      icon: <Person />,
      type: 'checkbox-text',
      options: [
        { value: 'only', label: 'I am the only decision maker' },
        { value: 'someone-else', label: 'Someone else' }
      ],
      validation: () => null // Optional
    },
    {
      id: 'address',
      question: (answers) => `Thanks,${answers.fullName ? ' ' + answers.fullName.split(' ')[0] : ''} and where is your ${answers.residenceType ? answers.residenceType.charAt(0).toUpperCase() + answers.residenceType.slice(1) : 'residence'} located?`,
      placeholder: "Enter address",
      icon: <LocationOn />,
      type: 'text',
      validation: (value) => value && value.trim().length > 0 ? null : 'Address is required'
    },
    {
      id: 'accessInfo',
      question: "Is there any information we need to know to access the property? Gate code, special directions? Etc.",
      placeholder: "Access info (optional)",
      icon: <Notes />,
      type: 'text',
      validation: () => null, // Optional
      skip: true
    }
  ];

  // Quote-specific questions for enhanced quote collection
  const quoteQuestions = [
    {
      id: 'serviceType',
      question: "What type of cleaning service do you need?",
      placeholder: "Select service type",
      icon: <CleaningServices />,
      type: 'select',
      options: [
        { value: 'one-time-clean', label: 'One-Time Deep Clean' },
        { value: 'weekly-clean', label: 'Weekly Cleaning' },
        { value: 'bi-weekly-clean', label: 'Bi-Weekly Cleaning' },
        { value: 'monthly-clean', label: 'Monthly Cleaning' },
        { value: 'move-in-out', label: 'Move In/Out Cleaning' },
        { value: 'vacation-rental-turnover-clean', label: 'Vacation Rental Turnover' }
      ],
      validation: (value) => value ? null : 'Please select a service type'
    },
    {
      id: 'propertyType',
      question: "What type of property is this?",
      placeholder: "Select property type",
      icon: <Home />,
      type: 'select',
      options: [
        { value: 'house', label: 'House' },
        { value: 'apartment', label: 'Apartment' },
        { value: 'condo', label: 'Condo' },
        { value: 'townhouse', label: 'Townhouse' },
        { value: 'office', label: 'Office' }
      ],
      validation: (value) => value ? null : 'Please select property type'
    },
    {
      id: 'bedrooms',
      question: "How many bedrooms does your property have?",
      placeholder: "Number of bedrooms",
      icon: <Bed />,
      type: 'number',
      min: 0,
      max: 10,
      validation: (value) => {
        const num = parseInt(value);
        return (num >= 0 && num <= 10) ? null : 'Please enter number of bedrooms (0-10)';
      }
    },
    {
      id: 'bathrooms',
      question: "How many bathrooms are there?",
      placeholder: "Number of bathrooms",
      icon: <Bathtub />,
      type: 'number',
      min: 1,
      max: 10,
      validation: (value) => {
        const num = parseInt(value);
        return (num >= 1 && num <= 10) ? null : 'Please enter number of bathrooms (1-10)';
      }
    },
    {
      id: 'specialInstructions',
      question: "Any special instructions or areas that need attention?",
      placeholder: "Tell us about any specific cleaning needs, pet areas, delicate items, etc.",
      icon: <Notes />,
      type: 'textarea',
      optional: true,
      validation: () => null // Optional field
    }
  ];

  // Combine questions based on mode
  const allQuestions = isQuoteMode ? [...questions, ...quoteQuestions] : questions;
  const currentQuestion = allQuestions[currentQuestionIndex];
  const currentValue = bookingState.customerInfo[currentQuestion.id] || '';
  const isLastQuestion = currentQuestionIndex === allQuestions.length - 1;

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Generate a secure random password for guest users
  const generateSecurePassword = () => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  // Create guest user in the system for data persistence
  const createGuestUser = async (guestData) => {
    try {
      const userData = {
        firstName: guestData.firstName,
        lastName: guestData.lastName,
        email: guestData.email,
        password: generateSecurePassword(),
        phone: guestData.phone,
        zipCode: guestData.zipCode,
        message: 'Guest user created from booking flow',
        isVerified: true
      };

      console.log('🔄 Creating guest user for data persistence...');
      const user = await createUser(userData);
      console.log('✅ Guest user created successfully:', user.email);
      return user;
    } catch (error) {
      console.warn('⚠️ Guest user creation failed:', error.response?.data?.error || error.message);
      return null;
    }
  };

  const handleFieldChange = (field) => (event) => {
    const value = typeof event === 'object' && event.target ? event.target.value : event;
    updateCustomerInfo({ [field]: value });
    clearErrors([field]);
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    
    if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return digits;
    }
  };

  const handlePhoneChange = (event) => {
    const formatted = formatPhoneNumber(event.target.value);
    updateCustomerInfo({ phone: formatted });
    clearErrors(['phone']);
  };

  const handleNext = () => {
    const validation = currentQuestion.validation(currentValue);
    
    if (validation) {
      setErrors({ [currentQuestion.id]: validation });
      return;
    }

    if (isLastQuestion) {
      handleContinue();
    } else {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setIsTransitioning(false);
      }, 300);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
        setIsTransitioning(false);
      }, 300);
    }
  };

  const handleContinue = async () => {
    clearErrors();
    setIsCreatingUser(true);
    
    try {
      if (isModal && onContinue) {
        const { firstName, lastName, email, phone, zipCode } = bookingState.customerInfo;
        const errors = {};
        
        allQuestions.forEach(q => {
          const validation = q.validation(bookingState.customerInfo[q.id]);
          if (validation) errors[q.id] = validation;
        });
        
        if (Object.keys(errors).length === 0) {
          await createGuestUser({ firstName, lastName, email, phone, zipCode });
          onContinue();
        } else {
          setErrors(errors);
        }
      } else {
        const validation = validateCurrentStep();
        
        if (validation.isValid) {
          const { firstName, lastName, email, phone, zipCode } = bookingState.customerInfo;
          await createGuestUser({ firstName, lastName, email, phone, zipCode });
          
          markStepCompleted(bookingState.currentStep);
          setCurrentStep(bookingState.currentStep + 1);
        } else {
          setErrors(validation.errors);
        }
      }
    } catch (error) {
      console.error('Error in guest flow:', error);
      if (isModal && onContinue) {
        onContinue();
      } else {
        markStepCompleted(bookingState.currentStep);
        setCurrentStep(bookingState.currentStep + 1);
      }
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleNext();
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      {/* Progress indicator */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center', gap: 1 }}>
        {questions.map((_, index) => (
          <Box
            key={index}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: index <= currentQuestionIndex ? 'primary.main' : 'grey.300',
              transition: 'all 0.3s ease'
            }}
          />
        ))}
      </Box>

      {/* Question display */}
      <Fade in={!isTransitioning} timeout={300}>
        <Box>
          {/* Question header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                mb: 2,
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' }
              }}
            >
              {currentQuestion.question}
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}
            >
              {currentQuestionIndex === 0 && "Let's start with your name to personalize your experience"}
              {currentQuestionIndex === 1 && "Great! Now we need your last name"}
              {currentQuestionIndex === 2 && "We'll use this to send you booking confirmations and updates"}
              {currentQuestionIndex === 3 && "For booking coordination and team communication"}
              {currentQuestionIndex === 4 && "This helps us provide accurate pricing for your area"}
            </Typography>
          </Box>

          {/* Input field */}
          <Card 
            sx={{ 
              mb: 4, 
              border: '2px solid',
              borderColor: bookingState.errors[currentQuestion.id] ? 'error.main' : 'transparent',
              '&:hover': {
                borderColor: 'primary.main',
                boxShadow: 3
              },
              transition: 'all 0.3s ease'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: 'primary.main', 
                    mr: 2,
                    width: 40,
                    height: 40
                  }}
                >
                  {currentQuestion.icon}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {currentQuestion.placeholder}
                </Typography>
              </Box>

              {currentQuestion.type === 'email' ? (
                <EmailAutocompleteTextField
                  fullWidth
                  type="email"
                  value={currentValue}
                  onChange={(value) => handleFieldChange(currentQuestion.id)(value)}
                  onKeyPress={handleKeyPress}
                  error={!!bookingState.errors[currentQuestion.id]}
                  helperText={bookingState.errors[currentQuestion.id]}
                  placeholder={currentQuestion.placeholder}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.1rem',
                      py: 1.5
                    }
                  }}
                />
              ) : currentQuestion.type === 'phone' ? (
                <TextField
                  fullWidth
                  value={currentValue}
                  onChange={handlePhoneChange}
                  onKeyPress={handleKeyPress}
                  error={!!bookingState.errors[currentQuestion.id]}
                  helperText={bookingState.errors[currentQuestion.id]}
                  placeholder={currentQuestion.placeholder}
                  inputProps={{ maxLength: 14 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.1rem',
                      py: 1.5
                    }
                  }}
                />
              ) : currentQuestion.type === 'select' ? (
                <FormControl fullWidth error={!!bookingState.errors[currentQuestion.id]}>
                  <Select
                    value={currentValue}
                    onChange={(e) => handleFieldChange(currentQuestion.id)(e.target.value)}
                    displayEmpty
                    sx={{
                      fontSize: '1.1rem',
                      py: 0.75
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>{currentQuestion.placeholder}</em>
                    </MenuItem>
                    {currentQuestion.options.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {bookingState.errors[currentQuestion.id] && (
                    <Box sx={{ color: 'error.main', fontSize: '0.875rem', mt: 0.5, ml: 1.75 }}>
                      {bookingState.errors[currentQuestion.id]}
                    </Box>
                  )}
                </FormControl>
              ) : currentQuestion.type === 'number' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
                  <IconButton 
                    onClick={() => {
                      const newValue = Math.max(currentQuestion.min, (parseInt(currentValue) || currentQuestion.min) - 1);
                      handleFieldChange(currentQuestion.id)(newValue.toString());
                    }}
                    disabled={parseInt(currentValue) <= currentQuestion.min}
                    sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}
                  >
                    <Remove />
                  </IconButton>
                  <TextField
                    type="number"
                    value={currentValue}
                    onChange={(e) => handleFieldChange(currentQuestion.id)(e.target.value)}
                    error={!!bookingState.errors[currentQuestion.id]}
                    helperText={bookingState.errors[currentQuestion.id]}
                    inputProps={{ 
                      min: currentQuestion.min, 
                      max: currentQuestion.max,
                      style: { textAlign: 'center', fontSize: '1.2rem', fontWeight: 600 }
                    }}
                    sx={{ 
                      width: 100,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1.1rem'
                      }
                    }}
                  />
                  <IconButton 
                    onClick={() => {
                      const newValue = Math.min(currentQuestion.max, (parseInt(currentValue) || 0) + 1);
                      handleFieldChange(currentQuestion.id)(newValue.toString());
                    }}
                    disabled={parseInt(currentValue) >= currentQuestion.max}
                    sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}
                  >
                    <Add />
                  </IconButton>
                </Box>
              ) : currentQuestion.type === 'textarea' ? (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={currentValue}
                  onChange={handleFieldChange(currentQuestion.id)}
                  error={!!bookingState.errors[currentQuestion.id]}
                  helperText={bookingState.errors[currentQuestion.id]}
                  placeholder={currentQuestion.placeholder}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.1rem',
                      py: 1.5
                    }
                  }}
                />
              ) : (
                <TextField
                  fullWidth
                  value={currentValue}
                  onChange={handleFieldChange(currentQuestion.id)}
                  onKeyPress={handleKeyPress}
                  error={!!bookingState.errors[currentQuestion.id]}
                  helperText={bookingState.errors[currentQuestion.id]}
                  placeholder={currentQuestion.placeholder}
                  inputProps={currentQuestion.id === 'zipCode' ? { maxLength: 5, pattern: '[0-9]{5}' } : undefined}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '1.1rem',
                      py: 1.5
                    }
                  }}
                />
              )}
            </CardContent>
          </Card>

          {/* Action buttons */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}>
            <Button
              variant="outlined"
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0}
              sx={{
                px: 3,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                display: currentQuestionIndex === 0 ? 'none' : 'block'
              }}
            >
              Back
            </Button>

            <Button
              variant="contained"
              onClick={handleNext}
              disabled={isCreatingUser}
              endIcon={isCreatingUser ? <CircularProgress size={20} /> : (isLastQuestion ? <Send /> : <ArrowForward />)}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                background: 'linear-gradient(45deg, #f9ce34, #ee2a7b)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(45deg, #f0c030, #e01a6b)',
                  transform: 'translateY(-2px)',
                  boxShadow: 3
                },
                transition: 'all 0.3s ease'
              }}
            >
              {isCreatingUser ? 'Creating Account...' : (isLastQuestion ? 'Get My Quote' : 'Continue')}
            </Button>
          </Box>

          {/* Summary of collected info */}
          {currentQuestionIndex > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                What we have so far:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {questions.slice(0, currentQuestionIndex).map((q) => {
                  const value = bookingState.customerInfo[q.id];
                  if (!value) return null;
                  
                  return (
                    <Chip
                      key={q.id}
                      icon={q.icon}
                      label={`${q.id === 'firstName' || q.id === 'lastName' ? '' : q.id + ': '}${value}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Fade>
    </Box>
  );
});

GuestInfoStep.displayName = 'GuestInfoStep';

export default GuestInfoStep;